import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { FileRequest, RequestTemplate } from '@/types/api';

export function useRequestTemplates() {
  return useQuery({
    queryKey: ['request-templates'],
    queryFn: async () =>
      (await api.get<RequestTemplate[]>('/admin/request-templates')).data,
  });
}

export function useCreateRequestTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      name: string;
      title: string;
      instructions?: string | null;
      default_due_in_days?: number | null;
    }) => {
      const { data } = await api.post<RequestTemplate>(
        '/admin/request-templates',
        vars,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['request-templates'] }),
  });
}

export function useUpdateRequestTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      name?: string;
      title?: string;
      instructions?: string | null;
      default_due_in_days?: number | null;
    }) => {
      const { id, ...rest } = vars;
      const { data } = await api.patch<RequestTemplate>(
        `/admin/request-templates/${id}`,
        rest,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['request-templates'] }),
  });
}

export function useDeleteRequestTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/request-templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['request-templates'] }),
  });
}

export function useInstantiateRequestTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      organization_id: string;
      target_folder_id?: string | null;
      due_date?: string | null;
    }) => {
      const { id, ...body } = vars;
      const { data } = await api.post<FileRequest>(
        `/admin/request-templates/${id}/instantiate`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['request-templates'] });
      qc.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}
